/**
 * The run's marks on the world — issue #117.
 *
 * `meta/save.ts` has always held both halves of the contract docs/01 §8 sets
 * out: a world is `(seed + generator)` plus a diff log of what the player did
 * to it, `recordCell`/`recordRect` write the log and `applyDiffLog` replays it.
 * `meta/runState.ts` wraps the writers as `noteCellChange`/`noteRectChange`.
 * **Every one of those five functions had zero callers**, so the log was empty
 * for every run ever played and the replay never ran. This file is the missing
 * middle: the thing that produces the log, and the thing that lays it back
 * down.
 *
 * Deliberately shaped like `RunBodies` (#84), because it answers the same
 * question one layer down and the answer should look the same. A crate's *pose*
 * is pure in `(seed, cx, cy)` and is re-derived; what the run did to it is a
 * run-owned diff. A cell's *material* is pure in `(seed, cx, cy)` and is
 * re-derived; what the run did to it is a run-owned diff. Same doctrine, same
 * two operations — adopt a run, decorate a generated chunk.
 *
 * ==== WHY THIS IS NOT ONLY A RESUME BUG =================================
 *
 * The ticket frames it as "resuming a run discards every hole you dug", which
 * is true and is the loudest symptom. It is not the whole of it. `CellWorld`
 * evicts chunks past 256 resident and past twelve chunks from the player
 * (`evictDistant`), and a re-entered chunk is regenerated from the seed. So
 * **without this file, walking two screens away and back already refilled every
 * hole**, inside a single unbroken session, in a game whose premise is a
 * destructible world. `RunBodies` says so about crates in its own header and
 * takes option 2 — run-owned, replayed on generation — for exactly this reason.
 * This is option 2 for cells.
 *
 * ==== WHAT IS RECORDED, AND WHAT IS NOT =================================
 *
 * **Only destruction, and only through `CellWorld.damageCell`.** That function
 * calls itself "the single entry point for all weapon/ability terrain
 * destruction, so hardness gating lives in exactly one place", and it already
 * fires `onCellDamaged` after the destruction lands — so hooking it catches
 * every swing, projectile, explosion, Breach, corrosive splash, burrowing
 * enemy and pressure rupture at once, with no site to remember.
 *
 * Everything the simulation does on its own reaches cells through `set`,
 * `swap` or `transmute` instead, and is deliberately **not** recorded: falling
 * sand, flowing liquid, fire, thermal melting, reaction products, support
 * collapse. That is not an omission, it is the promise `RunState`'s resume
 * table and docs/01 §8 already make — "the cave is the same cave, but
 * everything the simulation did inside it is gone".
 *
 * And it is what bounds the log. The ticket's worry is a log "that records
 * every cell a fire touches", which would be unbounded; a fire touches cells
 * through `transmute`, so it writes nothing here. A run can only write as many
 * entries as it can *destroy* cells, and destruction costs weapon swings.
 *
 * ==== THE COALESCING RULE ===============================================
 *
 * > **Cells destroyed on the same sim tick, on the same row, with the same
 * > resulting material, in a contiguous span, are one entry.**
 *
 * Nothing else is merged. This is the whole reason `noteRectChange` exists
 * beside `noteCellChange`, and it is enough on its own: destruction arrives in
 * rows, because every bulk destroyer in the game is a `damageCircle`, which
 * scans `for y … for x`. A radius-8 explosion writes ~200 cells and ~16
 * entries, so the rect form is worth about `r` to `1` and grows with the
 * weapon.
 *
 * It is implemented as an **open span**: the last entry this recorder wrote is
 * remembered, and the next cell either extends it (pop, re-record one wider) or
 * starts a new one. The log is therefore complete and correct at every instant
 * — there is no buffer to flush, and an autosave taken between two cells of the
 * same explosion loses nothing. The same property `RunBodies.flat` has, for the
 * same reason: a ledger you have to remember to flush is a ledger that
 * eventually is not flushed.
 *
 * `compactDiffLog` (20 000 entries) remains the backstop underneath, and drops
 * writes a later write provably shadows. The two do not overlap: this one
 * merges neighbours as they arrive, that one drops history that no longer
 * shows.
 */

import { noteCellChange, noteRectChange } from '../meta/runState';
import type { RunState } from '../meta/runState';
import { applyDiffLog } from '../meta/save';
import type { TerrainDiffEntry } from '../meta/save';
import { CHUNK_SHIFT, CHUNK_SIZE } from '../sim/world';
import type { CellWorld, ChunkGenerator } from '../sim/world';
import { SURFACE_MARK_MAX_Y } from './surfaceTerrain';

export class RunTerrain {
  private readonly cells: CellWorld;
  private run: RunState | null = null;

  /**
   * The entry this recorder wrote last, while it is still the log's last entry
   * and therefore still safe to widen. Null means the next cell starts a span.
   */
  private open: TerrainDiffEntry | null = null;

  /** Cells recorded since the run was adopted. Telemetry, and a smell. */
  recorded = 0;
  /** Cells laid back down over regenerated terrain. Telemetry. */
  replayed = 0;
  /**
   * Set when a log's seed did not match its run's. The marks belong to another
   * world and replaying them would carve a cave the player never dug, so they
   * are dropped — loudly enough for a test to see, quietly enough not to take
   * the run down.
   */
  seedMismatch = false;

  /**
   * Bounding box of every mark in the log, so a chunk the run has never been
   * near rejects in O(1) instead of O(entries).
   *
   * Measured, because the claim is a performance one. With a log at the
   * `DIFF_COMPACT_THRESHOLD` cap of 20 000 entries, generating a chunk that
   * contains none of them cost **0.53 ms** on top of 0.42 ms of generation —
   * more than the whole 0.3 ms frame-budget slack (#75), for a scan that
   * rejects every entry it looks at. A run deep enough to hold that many marks
   * is also generating chunks nowhere near most of them, which is exactly the
   * case this turns into four comparisons.
   *
   * Only ever **widened**, never shrunk, and recomputed from scratch on `bind`.
   * A box that is too large costs a scan; one that was too small would silently
   * skip a mark and restore the wrong world, so the asymmetry is the whole
   * safety argument. `compactDiffLog` can only remove entries, which can make
   * the box too large and never too small.
   */
  private minX = Infinity;
  private minY = Infinity;
  private maxX = -Infinity;
  private maxY = -Infinity;

  constructor(cells: CellWorld) {
    this.cells = cells;
    // Chained rather than assigned, exactly as `RunBodies` chains
    // `rigid.onBreak`: `game.ts` already points `onCellDamaged` at the
    // rigid-body layer's wake path, and silently unhooking it would stop
    // sleeping crates waking when you dig the floor out from under them —
    // i.e. break #42 in order to fix #117.
    const prev = cells.onCellDamaged;
    cells.onCellDamaged = (x, y) => {
      this.note(x, y);
      if (prev !== null) prev(x, y);
    };
  }

  // ------------------------------------------------------------ recording

  /**
   * A cell was destroyed. Read what is there *now* rather than what was:
   * `damageCell` either voids the cell or transmutes a brittle material into
   * its shattered form, and the log records the world's new state either way.
   */
  private note(x: number, y: number): void {
    const run = this.run;
    if (run === null) return;
    // The partition — `surfaceTerrain.ts`'s header owns the argument. Marks
    // above `y = 0` are the profile's: the frame there is seed-invariant, so
    // a hole in it is the same hole in every run, and a per-run log would
    // throw it away with the run (docs/27 §3.1).
    if (y < SURFACE_MARK_MAX_Y) return;
    const mat = this.cells.peek(x, y);
    const entries = run.terrain.entries;
    const open = this.open;
    const last = entries.length > 0 ? entries[entries.length - 1] : null;

    if (
      open !== null &&
      open === last &&
      open.tick === run.tick &&
      open.y === y &&
      open.mat === mat &&
      x === open.x + open.w
    ) {
      // Widen the span. Popping and re-recording rather than mutating the
      // entry in place keeps `meta/save.ts` the only code that decides what an
      // entry looks like — and keeps `noteRectChange` a function the game
      // calls rather than a shape this file open-codes.
      entries.pop();
      noteRectChange(run, open.x, open.y, open.w + 1, 1, mat);
    } else {
      noteCellChange(run, x, y, mat);
    }

    // Re-read off `run.terrain`, not off the `entries` captured above:
    // `recordCell`/`recordRect` compact the log past 20 000 entries and
    // **replace the array**, so the local is stale from here on and a widen
    // against it would extend an entry that is no longer last.
    const now = run.terrain.entries;
    this.open = now.length > 0 ? now[now.length - 1] : null;
    this.widen(x, y, 1, 1);
    this.recorded++;
  }

  /** Grow the reject box. Widen-only — see the field's docblock. */
  private widen(x: number, y: number, w: number, h: number): void {
    if (x < this.minX) this.minX = x;
    if (y < this.minY) this.minY = y;
    if (x + w > this.maxX) this.maxX = x + w;
    if (y + h > this.maxY) this.maxY = y + h;
  }

  // -------------------------------------------------------------- binding

  /**
   * Adopt a run: replay what it already carries, then record into it.
   *
   * Called after `createRunState` and after a resume, like `RunBodies.bind`.
   * For a fresh run the log is empty and this does nothing but arm the
   * recorder; for a resumed one it is the moment the player's holes come back.
   *
   * Chunks generated *before* this point — the spawn neighbourhood, which
   * `game.ts` grounds the player against — are replayed here directly. Every
   * chunk generated afterwards is caught by `withRunDiffs`. Between the two
   * there is no window in which a mark can be missed.
   */
  bind(run: RunState): void {
    this.run = run;
    this.open = null;
    this.minX = Infinity;
    this.minY = Infinity;
    this.maxX = -Infinity;
    this.maxY = -Infinity;
    this.seedMismatch = run.terrain.seed !== (run.seed >>> 0);
    if (this.seedMismatch) {
      // A log against another seed is not repairable and is not the run's
      // history either. Drop it rather than carve someone else's cave.
      run.terrain.entries.length = 0;
      run.terrain.seed = run.seed >>> 0;
      return;
    }
    for (const e of run.terrain.entries) this.widen(e.x, e.y, e.w, e.h);
    let n = 0;
    applyDiffLog(run.terrain, {
      setCell: (x, y, mat) => {
        // Resident chunks only. Generating a chunk here to write one cell into
        // it would drag the whole dug volume into memory at load, and
        // `withRunDiffs` covers it for free the moment the player goes there.
        if (this.cells.peekChunkAt(x, y) === null) return;
        this.cells.set(x, y, mat);
        n++;
      },
    });
    this.replayed += n;
  }

  /** Let go of the run. Its marks name cells in a world that is being thrown away. */
  clear(): void {
    this.run = null;
    this.open = null;
    this.minX = Infinity;
    this.minY = Infinity;
    this.maxX = -Infinity;
    this.maxY = -Infinity;
  }

  // --------------------------------------------------------------- replay

  /**
   * Lay this run's marks back over a chunk that has just been generated.
   *
   * A linear scan of the log per generated chunk, deliberately, with no index
   * to keep in step. The alternative — a `chunkKey -> entries` map — has to be
   * maintained through the pop-and-re-record above and through
   * `compactDiffLog` rebuilding the array underneath it, and a stale index
   * here restores the *wrong* world, which is worse than the bug being fixed.
   *
   * What makes the scan affordable is the reject box above, not the scan being
   * cheap. Measured against a log at the 20 000-entry compaction cap: 0.53 ms
   * per generated chunk without the box, four comparisons with it. Inside the
   * box the cost is proportional to the marks that are genuinely in the chunk,
   * which is inherent — those cells have to be written.
   */
  decorate(cx: number, cy: number): void {
    const run = this.run;
    if (run === null) return;
    const x0 = cx << CHUNK_SHIFT;
    const y0 = cy << CHUNK_SHIFT;
    const x1 = x0 + CHUNK_SIZE;
    const y1 = y0 + CHUNK_SIZE;
    if (x1 <= this.minX || x0 >= this.maxX || y1 <= this.minY || y0 >= this.maxY) return;
    for (const e of run.terrain.entries) {
      if (e.x >= x1 || e.y >= y1 || e.x + e.w <= x0 || e.y + e.h <= y0) continue;
      const ax0 = e.x > x0 ? e.x : x0;
      const ay0 = e.y > y0 ? e.y : y0;
      const ax1 = e.x + e.w < x1 ? e.x + e.w : x1;
      const ay1 = e.y + e.h < y1 ? e.y + e.h : y1;
      for (let y = ay0; y < ay1; y++) {
        for (let x = ax0; x < ax1; x++) {
          // `cells.set` rather than a raw array write: it is the authoritative
          // write, and it resets temperature, flags and aux the way generation
          // does. Re-entrancy is not a risk — `getChunk` inserts the chunk into
          // the map *before* calling the generator, so this resolves to the
          // chunk being decorated and generates nothing.
          this.cells.set(x, y, e.mat);
          this.replayed++;
        }
      }
    }
  }
}

/**
 * The generator decorator, in the shape `withSupportSites` / `withCrateSites` /
 * `withDoorSites` already established.
 *
 * Belongs **outermost** of the four: the player's marks are the last thing that
 * happened to the world, so they go on last. A crate site stamped over a hole
 * the player blew in the floor would stand a crate in mid-air; a hole replayed
 * over the crate site is simply the floor the player removed.
 */
export function withRunDiffs(inner: ChunkGenerator, terrain: RunTerrain): ChunkGenerator {
  return (chunk, cx, cy) => {
    inner(chunk, cx, cy);
    terrain.decorate(cx, cy);
  };
}
